import { useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Box, Modal } from '@mui/material';

interface DownloadPDFModalProps {
  isOpen: boolean;
  handleClose: () => void;
  fileName: string;
  children: React.ReactNode;
  isSinglePage?: boolean;
  delay?: number;
}

const DownloadPDFModal: React.FC<DownloadPDFModalProps> = ({
  isOpen,
  handleClose,
  fileName,
  children,
  isSinglePage = false,
  delay = 700,
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);

  const downloadMultiPagePDF = async () => {
    const content = contentRef.current;
    if (!content) return;

    const canvas = await html2canvas(content, {
      scale: 1.5,
      useCORS: true,
      scrollX: 0,
      scrollY: 0,
      windowWidth: document.documentElement.scrollWidth,
      windowHeight: content.scrollHeight,
    });

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    const pdf = new jsPDF('p', 'mm', 'a4', true);
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 0;
    let yPos = margin;
    let pageY = 0;
    const pageCanvasHeight = (pageHeight - yPos - margin) * (canvas.width / pageWidth);

    while (pageY < canvas.height) {
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = Math.min(pageCanvasHeight, canvas.height - pageY);

      const pageCtx = pageCanvas.getContext('2d', { willReadFrequently: true });
      if (pageCtx) {
        pageCtx.drawImage(
          canvas,
          0,
          pageY,
          canvas.width,
          pageCanvas.height,
          0,
          0,
          canvas.width,
          pageCanvas.height,
        );

        const imgData = pageCanvas.toDataURL('image/jpeg', 0.6);
        pdf.addImage(
          imgData,
          'JPEG',
          margin,
          yPos,
          pageWidth - 2 * margin,
          (pageCanvas.height * (pageWidth - 2 * margin)) / canvas.width,
        );
      }

      pageY += pageCanvasHeight;
      if (pageY < canvas.height) pdf.addPage();
    }

    pdf.save(fileName);
    handleClose();
  };

  const downloadSinglePagePDF = async () => {
    const content = contentRef.current;
    if (!content) return;

    const canvas = await html2canvas(content, {
      scale: 1.5,
      useCORS: true,
      scrollX: 0,
      scrollY: 0,
      windowWidth: document.documentElement.scrollWidth,
      windowHeight: content.scrollHeight,
    });

    const imgData = canvas.toDataURL('image/jpeg', 1.0);

    // Calculate PDF dimensions to match the canvas
    const pdf = new jsPDF('p', 'mm', 'a4', true);
    const pageWidth = 210; // A4 width in mm

    // Calculate height to maintain aspect ratio
    const pageHeight = (canvas.height * pageWidth) / canvas.width;

    // Create custom page size to fit content
    pdf.internal.pageSize.height = pageHeight;
    pdf.internal.pageSize.width = pageWidth;

    pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight);

    pdf.save(fileName);
    handleClose();
  };

  useEffect(() => {
    if (isOpen) {
      const timeout = setTimeout(
        isSinglePage ? downloadSinglePagePDF : downloadMultiPagePDF,
        delay,
      );
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  return (
    <Modal open={isOpen} onClose={handleClose} hideBackdrop>
      <Box
        ref={contentRef}
        sx={{
          position: 'absolute',
          top: '-9999px',
          left: '-9999px',
          transform: 'translate(-50%, -50%)',
          width: 1200,
          bgcolor: '#F0F0F1',
          p: 4,
          boxShadow: 24,
        }}
      >
        {children}
      </Box>
    </Modal>
  );
};

export default DownloadPDFModal;
